import React, { useState, useRef, useEffect } from 'react';
import { IonButton, IonIcon } from '@ionic/react';
import { keypadOutline, timeOutline } from 'ionicons/icons';
import styles from './GcalTimePicker.module.scss';
import { useTranslation } from 'react-i18next';

interface Props {
  value: string; // "HH:MM" 24hr
  onSelect: (time: string) => void;
  onClose: () => void;
  desktop?: boolean;
}

const CX = 110, CY = 110, R = 80;

function polarToXY(deg: number, r: number) {
  const rad = (deg - 90) * (Math.PI / 180);
  return { x: CX + r * Math.cos(rad), y: CY + r * Math.sin(rad) };
}

const GcalTimePicker: React.FC<Props> = ({ value, onSelect, onClose, desktop = false }) => {
  const { t: tr } = useTranslation();
  const h24    = parseInt(value.split(':')[0] ?? '9', 10);
  const initM  = parseInt(value.split(':')[1] ?? '0', 10);
  const initH  = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;

  const [mode,     setMode]     = useState<'hours' | 'minutes'>('hours');
  const [hour,     setHour]     = useState(initH);
  const [minute,   setMinute]   = useState(initM);
  const [isPM,     setIsPM]     = useState(h24 >= 12);
  const [keyboard, setKeyboard] = useState(false);
  const [manualH,  setManualH]  = useState(initH.toString().padStart(2, '0'));
  const [manualM,  setManualM]  = useState(initM.toString().padStart(2, '0'));

  const svgRef    = useRef<SVGSVGElement>(null);
  const modeRef   = useRef(mode);
  const dragStart = useRef<{ x: number; y: number } | null>(null);
  const wasDrag   = useRef(false);
  useEffect(() => { modeRef.current = mode; }, [mode]);

  const confirm = (h: number, m: number, pm: boolean) => {
    const out = h % 12 + (pm ? 12 : 0);
    onSelect(`${out.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
  };

  const getValueFromCoords = (clientX: number, clientY: number, svg: SVGSVGElement, currentMode: 'hours' | 'minutes') => {
    const rect  = svg.getBoundingClientRect();
    const scale = 220 / rect.width;
    const dx    = (clientX - rect.left) * scale - CX;
    const dy    = (clientY - rect.top)  * scale - CY;
    const dist  = Math.sqrt(dx * dx + dy * dy);
    if (dist < 20) return null;
    const angle = ((Math.atan2(dy, dx) * 180 / Math.PI) + 90 + 360) % 360;
    return currentMode === 'hours'
      ? (Math.round(angle / 30) || 12)
      : Math.round(angle / 6) % 60;
  };

  // ── Native touch listeners (passive:false required) ──────────────────────
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const onTouchStart = (e: TouchEvent) => {
      e.preventDefault();
      const t = e.touches[0];
      dragStart.current = { x: t.clientX, y: t.clientY };
      wasDrag.current   = false;
      const val = getValueFromCoords(t.clientX, t.clientY, svg, modeRef.current);
      if (val === null) return;
      if (modeRef.current === 'hours') setHour(val);
      else setMinute(val);
    };

    const onTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const t = e.touches[0];
      if (dragStart.current) {
        const d = Math.hypot(t.clientX - dragStart.current.x, t.clientY - dragStart.current.y);
        if (d > 4) wasDrag.current = true;
      }
      const val = getValueFromCoords(t.clientX, t.clientY, svg, modeRef.current);
      if (val === null) return;
      if (modeRef.current === 'hours') setHour(val);
      else setMinute(val);
    };

    const onTouchEnd = (e: TouchEvent) => {
      const t = e.changedTouches[0];
      const val = getValueFromCoords(t.clientX, t.clientY, svg, modeRef.current);
      if (val !== null && modeRef.current === 'hours') {
        setHour(val);
        setMode('minutes');
      }
      dragStart.current = null;
      wasDrag.current   = false;
    };

    svg.addEventListener('touchstart', onTouchStart, { passive: false });
    svg.addEventListener('touchmove',  onTouchMove,  { passive: false });
    svg.addEventListener('touchend',   onTouchEnd,   { passive: false });
    return () => {
      svg.removeEventListener('touchstart', onTouchStart);
      svg.removeEventListener('touchmove',  onTouchMove);
      svg.removeEventListener('touchend',   onTouchEnd);
    };
  }, []);

  // ── Mouse handlers ───────────────────────────────────────────────────────
  const onMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    e.preventDefault();
    dragStart.current = { x: e.clientX, y: e.clientY };
    wasDrag.current   = false;
    if (!svgRef.current) return;
    const val = getValueFromCoords(e.clientX, e.clientY, svgRef.current, mode);
    if (val === null) return;
    if (mode === 'hours') setHour(val);
    else setMinute(val);
  };

  const onMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!dragStart.current || !svgRef.current) return;
    const d = Math.hypot(e.clientX - dragStart.current.x, e.clientY - dragStart.current.y);
    if (d > 4) wasDrag.current = true;
    if (!wasDrag.current) return;
    const val = getValueFromCoords(e.clientX, e.clientY, svgRef.current, mode);
    if (val === null) return;
    if (mode === 'hours') setHour(val);
    else setMinute(val);
  };

  const onMouseUp = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current) return;
    const val = getValueFromCoords(e.clientX, e.clientY, svgRef.current, mode);
    if (val !== null && mode === 'hours') {
      setHour(val);
      setMode('minutes');
    }
    dragStart.current = null;
    wasDrag.current   = false;
  };

  // ── Derived visuals ──────────────────────────────────────────────────────
  const numR     = R - 4; // same ring radius for both hours and minutes
  const selAngle = mode === 'hours' ? (hour % 12) * 30 : minute * 6;
  const tipPos   = polarToXY(selAngle, numR); // hand tip lands exactly on number ring
  const hourNums = Array.from({ length: 12 }, (_, i) => i + 1);
  const minNums  = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  return (
    <div className={`${styles.picker}${desktop ? ` ${styles.desktop}` : ''}`}>

      {/* ── Header ── */}
      <div className={styles.header}>
        <div className={styles.timeRow}>
          <button
            className={`${styles.seg} ${mode === 'hours' ? styles.segActive : ''}`}
            onClick={() => setMode('hours')}
          >{hour.toString().padStart(2, '0')}</button>
          <span className={styles.colon}>:</span>
          <button
            className={`${styles.seg} ${mode === 'minutes' ? styles.segActive : ''}`}
            onClick={() => setMode('minutes')}
          >{minute.toString().padStart(2, '0')}</button>
        </div>
        <div className={styles.period}>
          <button className={`${styles.periodBtn} ${!isPM ? styles.periodActive : ''}`} onClick={() => setIsPM(false)}>{tr('components.GcalTimePicker.am')}</button>
          <button className={`${styles.periodBtn} ${isPM  ? styles.periodActive : ''}`} onClick={() => setIsPM(true)}>{tr('components.GcalTimePicker.pm')}</button>
        </div>
      </div>

      {/* ── Clock or keyboard ── */}
      {keyboard ? (
        <div className={styles.kbArea}>
          <div className={styles.kbRow}>
            <input className={styles.kbInput} type="number" min={1} max={12}
              value={manualH} onChange={e => setManualH(e.target.value)} placeholder={tr('components.GcalTimePicker.hh')} />
            <span className={styles.kbColon}>:</span>
            <input className={styles.kbInput} type="number" min={0} max={59}
              value={manualM} onChange={e => setManualM(e.target.value)} placeholder={tr('components.GcalTimePicker.mm')} />
          </div>
        </div>
      ) : (
        <svg
          ref={svgRef}
          className={styles.clock}
          viewBox="0 0 220 220"
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={() => { dragStart.current = null; wasDrag.current = false; }}
          style={{ cursor: 'pointer', touchAction: 'none' }}
        >
          {/* Face */}
          <circle cx={CX} cy={CY} r={R + 20} className={styles.face} />

          {/* Hand + tip — drawn before numbers so numbers always render on top */}
          <line x1={CX} y1={CY} x2={tipPos.x} y2={tipPos.y} className={styles.hand} />
          <circle cx={CX} cy={CY} r={4} className={styles.handDot} />
          <circle cx={tipPos.x} cy={tipPos.y} r={16} className={styles.handTip} />
          {/* Small white dot inside the tip — minutes mode only */}
          {mode === 'minutes' && <circle cx={tipPos.x} cy={tipPos.y} r={3} fill="#fff" opacity="0.8" />}

          {/* Numbers — always on top of the hand tip so they stay readable */}
          {(mode === 'hours' ? hourNums : minNums).map(num => {
            const deg    = mode === 'hours' ? num * 30 : num * 6;
            const pos    = polarToXY(deg, numR);
            const active = mode === 'hours' ? num === hour : num === minute;
            return (
              <text key={num}
                x={pos.x} y={pos.y}
                textAnchor="middle"
                dominantBaseline="central"
                className={`${styles.numText} ${active ? styles.numTextActive : ''}`}
                style={mode === 'minutes' ? { fontSize: '13px', fontWeight: active ? 600 : 400 } : undefined}
              >
                {mode === 'hours' ? num : num.toString().padStart(2, '0')}
              </text>
            );
          })}
        </svg>
      )}

      {/* ── Footer ── */}
      <div className={styles.footer}>
        <IonButton fill="clear" size="small" onClick={() => setKeyboard(k => !k)}>
          <IonIcon icon={keyboard ? timeOutline : keypadOutline} slot="icon-only" />
        </IonButton>
        <IonButton fill="clear" size="small" color="medium" onClick={onClose}>{tr('components.GcalTimePicker.cancel')}</IonButton>
        <IonButton fill="clear" size="small" onClick={() => {
          if (keyboard) {
            const h = parseInt(manualH, 10);
            const m = parseInt(manualM, 10);
            if (h >= 1 && h <= 12 && m >= 0 && m <= 59) confirm(h, m, isPM);
          } else {
            confirm(hour, minute, isPM);
          }
        }}>{tr('components.GcalTimePicker.ok')}</IonButton>
      </div>

    </div>
  );
};

export default GcalTimePicker;
